class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (event) => {
      const checkoutBtn = event.target.closest('#CartDrawer-Checkout');
      if (checkoutBtn) {
        event.preventDefault();
        const cartForm = this.querySelector('#CartDrawer-Form');
        if (cartForm) {
          cartForm.insertAdjacentHTML('beforeend', '<input type="hidden" name="checkout" value="Checkout">');
          cartForm.submit();
        }
      }
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.onCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onCartUpdate(event) {
    if (event?.cartData?.sections && (event.cartData.sections['cart-drawer'] || event.cartData.sections['CartDrawer'])) {
      this.renderContents(event.cartData);
      return;
    }

    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.renderContents(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        selector: '[data-cart-items]',
      },
      {
        selector: '[data-cart-discounts]',
      },
      {
        selector: '[data-cart-totals]',
      },
      {
        selector: '[data-cart-button]',
      },
      {
        selector: '#cart-icon-bubble',
      },
      {
        selector: '[data-cart-empty]',
      },
    ];
  }

  renderContents(parsedState) {
    const isDOMParsing = parsedState instanceof HTMLDocument;
    const sectionId = 'cart-drawer';
    
    let html;
    if (isDOMParsing) {
      html = parsedState;
    } else {
      const sectionHtml = parsedState.sections[sectionId] || parsedState.sections['CartDrawer'] || Object.values(parsedState.sections)[0];
      if (typeof sectionHtml === 'string') {
        html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      } else {
        html = sectionHtml; // Already a DOM
      }
    }
    
    if (!html) return;

    // Granular Update: Target specific UI blocks to avoid blank screens
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState?.sections?.[section.id || section.section] || parsedState?.sections?.['cart-icon-bubble'];
      
      let source;
      if (section.selector === '#cart-icon-bubble' && sectionHtml) {
        source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(section.selector);
      } else {
        source = html.querySelector(section.selector);
      }
      
      const target = document.querySelector(section.selector); // Look in the whole document for the bubble
      
      if (source && target) {
        target.innerHTML = source.innerHTML;
        // Also sync the display style for the empty state container
        if (section.selector === '[data-cart-empty]') {
          target.style.display = source.style.display;
        }
      }
    });

    // Handle is-empty class on the main wrapper for global styling/visibility rules
    const cartItems = this.querySelector('.cart-item');
    const isEmpty = !cartItems;
    this.classList.toggle('is-empty', isEmpty);

    if (!isDOMParsing) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector) || doc.querySelector('.shopify-section') || doc.body;
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.js-contents',
      },
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
